import React, { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import WordProgress from '@/features/letterParty/components/controlBar/WordProgress';
import AudioToggleButton from '@/shared/components/audioToggle/AudioToggleButton';
import Button from '@/shared/components/button/Button';

import type { MutableRefObject } from 'react';
import type { WordProgressHandle } from '@/features/letterParty/components/controlBar/WordProgress';

interface ControlBarProps {
	onBack: () => void;
	currentWord: string;
	revealedLetters: string;
	onSpeak: (word: string, forceSpeak?: boolean) => void;
	wordProgressRef: MutableRefObject<WordProgressHandle | null>;
	isWordComplete: boolean;
	questionsRemaining: number;
	isWordAudioPlaying: boolean;
}

const ControlBar = ({
	onBack,
	currentWord,
	revealedLetters,
	onSpeak,
	wordProgressRef,
	isWordComplete,
	questionsRemaining,
	isWordAudioPlaying,
}: ControlBarProps) => {
	const { t } = useTranslation();
	const controlBarRef = useRef<HTMLDivElement | null>(null);
	const hasRemainingQuestions = questionsRemaining > 0;

	return (
		<div className="letter-party-game__control-bar" ref={controlBarRef}>
			<div className="container">
				<div className="letter-party-game__control-left">
					<Button
						type="secondary"
						color="white"
						rounded
						iconName="icon-arrow-left"
						className="letter-party-game__control-button"
						onClick={onBack}
					/>
					<AudioToggleButton
						tooltip={t('letter_party.audio_toggle.tooltip')}
						className="letter-party-game__control-button letter-party-game__mute-button-desktop"
					/>
				</div>
				<div className="letter-party-game__control-center">
					<WordProgress
						ref={wordProgressRef}
						controlBarRef={controlBarRef}
						currentWord={currentWord}
						revealedLetters={revealedLetters}
						onSpeak={onSpeak}
						isComplete={isWordComplete}
						isWordAudioPlaying={isWordAudioPlaying}
					/>
				</div>
				<div className="letter-party-game__control-right">
					{hasRemainingQuestions && (
						<span className="letter-party-game__questions-remaining">
							{t('letter_party.remaining', { count: questionsRemaining })}
						</span>
					)}
					<AudioToggleButton
						tooltip={t('letter_party.audio_toggle.tooltip')}
						className="letter-party-game__control-button letter-party-game__mute-button-mobile"
					/>
				</div>
			</div>
		</div>
	);
};

export default memo(ControlBar);
